/**
 * Firebase — nachgeladen statt statisch gebündelt.
 *
 * Warum: @firebase/* wog rund 114 KB unkomprimiert im Haupt-Bundle, das auf JEDER
 * öffentlichen Landingpage geladen wird. Ein anonymer Besucher, der „gumroad
 * alternative" googelt, lud damit ein komplettes Auth-SDK, um statischen Text zu
 * lesen. Seit diesem Modul gilt:
 *
 *   - Anonyme Besucher laden Firebase NIE. Ob eine Sitzung existiert, verrät der
 *     Marker in localStorage (SESSION_FLAG) — gesetzt beim Login, gelöscht beim
 *     Logout. Ohne Marker gilt: nicht eingeloggt, kein Import.
 *   - Erst ein Login-Versuch oder ein vorhandener Marker lädt das SDK nach.
 *
 * Der Marker ist bewusst nur ein Hinweis, keine Wahrheit: Die Wahrheit bleibt
 * Firebase selbst. Fehlt der Marker, obwohl eine Firebase-Sitzung existiert
 * (z. B. localStorage manuell geleert), sieht der Nutzer sich ausgeloggt und
 * meldet sich neu an — der Marker ist danach wieder korrekt. Umgekehrt schadet
 * ein verwaister Marker nicht: Firebase lädt, meldet „kein User", der Marker
 * wird verworfen.
 *
 * getStorage() ist bewusst entfernt — es wurde nirgends importiert und zog
 * @firebase/storage (rund 11 KB) mit ins Bundle.
 *
 * Aufrufer nutzen getAuth()/getAuthModule() — beide async.
 */

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/** Hinweis darauf, dass dieser Browser schon einmal eingeloggt war. */
export const SESSION_FLAG = 'monemee_has_session';

/**
 * Merker, dass dieser Browser den Marker oben überhaupt schon einmal
 * gesetzt bekommen hat.
 *
 * Nötig für den Übergang: Nutzer, die zum Zeitpunkt des Deploys eingeloggt
 * waren, haben eine gültige Firebase-Sitzung in IndexedDB, aber noch keinen
 * SESSION_FLAG — den gab es vorher nicht. Ohne diesen zweiten Merker würden
 * sie alle als „nicht eingeloggt" behandelt und müssten sich neu anmelden.
 *
 * Deshalb gilt: Solange dieser Merker fehlt, wird Firebase EINMAL geladen und
 * nach der Wahrheit gefragt. Danach steht fest, ob eine Sitzung existiert, und
 * anonyme Besucher laden das SDK nie wieder.
 */
export const SESSION_CHECKED_FLAG = 'monemee_session_checked';

let loadPromise = null;

/** Merkt, dass eine Sitzung besteht — damit Firebase beim nächsten Start geladen wird. */
export function markSessionActive() {
  try {
    localStorage.setItem(SESSION_FLAG, '1');
  } catch {
    // Private Mode o. Ä.: dann wird Firebase eben erst beim nächsten Login geladen.
  }
}

/** Entfernt den Marker (Logout, oder Firebase meldet „kein User"). */
export function clearSessionFlag() {
  try {
    localStorage.removeItem(SESSION_FLAG);
  } catch {
    /* egal */
  }
}

/**
 * Könnte hier eine Sitzung liegen? Entscheidet, ob beim Start geladen wird.
 *
 * Zwei Fälle liefern true:
 *  - Der Marker ist gesetzt: dieser Browser war eingeloggt.
 *  - Der Browser wurde noch nie geprüft: könnte eine Sitzung aus der Zeit vor
 *    dem Lazy-Loading haben (siehe SESSION_CHECKED_FLAG). Einmalig nachsehen.
 */
export function hasPossibleSession() {
  try {
    if (localStorage.getItem(SESSION_FLAG) === '1') return true;
    return localStorage.getItem(SESSION_CHECKED_FLAG) !== '1';
  } catch {
    // Ohne localStorage lässt sich nichts merken. Dann lieber laden und
    // korrekt anzeigen als jemanden fälschlich ausloggen.
    return true;
  }
}

/**
 * Merker, dass gerade ein signInWithRedirect gestartet wurde.
 *
 * Ohne ihn muss beim Mount blind getRedirectResult() gefragt werden — und jeder
 * Fehler dieses Aufrufs (auf iOS Safari liefert der ITP-bedingt regelmäßig
 * einen) landete als Fehlerbanner beim Nutzer, obwohl gar kein Redirect-Login
 * lief und der eigentliche Popup-Login sauber durchging.
 */
export const REDIRECT_PENDING_FLAG = 'monemee_redirect_pending';

/** Vor signInWithRedirect setzen — die Seite wird gleich verlassen. */
export function markRedirectPending() {
  try {
    localStorage.setItem(REDIRECT_PENDING_FLAG, '1');
  } catch {
    // Ohne Marker wird das Redirect-Ergebnis nach der Rückkehr nicht
    // ausgewertet; onAuthStateChanged übernimmt den User trotzdem.
  }
}

/** Läuft gerade ein Redirect-Login? */
export function hasRedirectPending() {
  try {
    return localStorage.getItem(REDIRECT_PENDING_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Nach dem Auswerten des Redirect-Ergebnisses aufräumen. */
export function clearRedirectPending() {
  try {
    localStorage.removeItem(REDIRECT_PENDING_FLAG);
  } catch {
    /* egal */
  }
}

/**
 * Hält fest, dass dieser Browser einmal gegen Firebase geprüft wurde.
 * Ab jetzt entscheidet allein SESSION_FLAG.
 */
export function markSessionChecked() {
  try {
    localStorage.setItem(SESSION_CHECKED_FLAG, '1');
  } catch {
    /* egal — dann wird eben beim nächsten Start erneut geprüft */
  }
}

/**
 * Lädt Firebase App + Auth genau einmal und initialisiert sie.
 * Jeder weitere Aufruf bekommt dieselbe Promise.
 */
function loadFirebase() {
  if (!loadPromise) {
    loadPromise = Promise.all([
      import(/* webpackChunkName: "firebase-auth" */ 'firebase/app'),
      import(/* webpackChunkName: "firebase-auth" */ 'firebase/auth'),
    ]).then(([appModule, authModule]) => {
      const app = appModule.initializeApp(firebaseConfig);
      const auth = authModule.getAuth(app);

      // Sprache für Firebase-System-Mails (Passwort-Reset, E-Mail-Verifizierung).
      // Quelle: URL-Präfix (/en → en), konsistent mit der App-Sprache. Ein
      // Sprachwechsel ist immer ein Hard-Reload, daher reicht die Auswertung beim Init.
      auth.languageCode =
        window.location.pathname === '/en' || window.location.pathname.startsWith('/en/')
          ? 'en'
          : 'de';

      return { app, auth, authModule };
    });
  }
  return loadPromise;
}

/** Die Auth-Instanz (lädt Firebase bei Bedarf nach). */
export async function getAuth() {
  return (await loadFirebase()).auth;
}

/**
 * Das gesamte firebase/auth-Modul — für Aufrufer, die Funktionen wie
 * signInWithEmailAndPassword brauchen, die früher statisch importiert wurden.
 */
export async function getAuthModule() {
  return (await loadFirebase()).authModule;
}

/**
 * Auth-Instanz NUR, wenn Firebase ohnehin schon geladen ist oder eine Sitzung
 * wahrscheinlich ist — sonst null. Für Pfade, die einen eingeloggten User
 * erwarten, aber für anonyme Besucher auf keinen Fall einen 114-KB-Download
 * auslösen dürfen (z. B. der Token-Interceptor in services/api.js).
 */
export async function getAuthIfLoaded() {
  if (!loadPromise && !hasPossibleSession()) return null;
  return (await loadFirebase()).auth;
}
